import { useApp } from "../store.jsx";
import { portfolioSummary, fmtSek, fmtMonths, fmtHours } from "../calc.js";

export default function HomePage() {
  const { tasks, settings, meta } = useApp();
  if (!tasks || !settings || !meta) return <div className="container">Loading…</div>;

  const activeTasks   = tasks.filter(t => t.active !== false);
  const inactiveCount = tasks.length - activeTasks.length;
  const totalsByStatus  = { Live:0, Building:0, Planned:0, Future:0 };
  const totalsByPrio    = { P1:0, P2:0, P3:0, P4:0 };
  activeTasks.forEach(t => { totalsByStatus[t.status] = (totalsByStatus[t.status]||0)+1; totalsByPrio[t.priority] = (totalsByPrio[t.priority]||0)+1; });

  // portfolioSummary filters inactive tasks itself, but pass `tasks` for clarity.
  const port = portfolioSummary(tasks, settings);

  return (
    <div className="container">
      <div className="hero">
        <h1>NYCE AI Strategy — Implementation Plan</h1>
        <div className="sub">29 initiatives across Sales · PS · PD, sequenced May 2026 → Dec 2027.
          Adjust effort, FTE and impact scores on any task to see the portfolio recalculate live.</div>
      </div>

      <div className="kpi-row">
        <div className="kpi">
          <div className="l">Total cost to build</div>
          <div className="v">{fmtSek(port.totalCostSek)}</div>
          <div className="s">one-time, sum of all tasks</div>
        </div>
        <div className="kpi">
          <div className="l">Monthly value at full deployment</div>
          <div className="v positive">{fmtSek(port.totalMonthlyValueSek)}</div>
          <div className="s">recurring once everything is live</div>
        </div>
        <div className="kpi">
          <div className="l">Monthly hours saved</div>
          <div className="v positive">{fmtHours(port.totalMonthlyHoursSaved)}</div>
          <div className="s">
            {settings.hoursPerFteMonth > 0
              ? `≈ ${(port.totalMonthlyHoursSaved / settings.hoursPerFteMonth).toFixed(1)} FTE-equivalent`
              : "h/mo across active tasks"}
          </div>
        </div>
        <div className="kpi">
          <div className="l">Saved-hours value /mo</div>
          <div className="v positive">{fmtSek(port.totalHoursSavedValueSek)}</div>
          <div className="s">hours saved × hourly rate</div>
        </div>
        <div className="kpi">
          <div className="l">New revenue /mo</div>
          <div className="v positive">{fmtSek(port.totalNewRevenueMonthlySek)}</div>
          <div className="s">recurring once live</div>
        </div>
        <div className="kpi">
          <div className="l">New revenue to Dec 2027</div>
          <div className="v positive">{fmtSek(port.totalNewRevenueHorizonSek)}</div>
          <div className="s">cumulative while live</div>
        </div>
        <div className="kpi">
          <div className="l">Avg impact score</div>
          <div className="v">{port.avgScore.toFixed(1)}</div>
          <div className="s">strategy + quality + customer / 3</div>
        </div>
        <div className="kpi">
          <div className="l">Portfolio break-even</div>
          <div className="v">{fmtMonths(port.portfolioBreakevenMonths)}</div>
          <div className="s">if all tasks ran simultaneously</div>
        </div>
        <div className="kpi">
          <div className="l">Dec 2027 net value</div>
          <div className={"v " + (port.totalHorizonEndValueSek >= 0 ? "positive" : "negative")}>
            {fmtSek(port.totalHorizonEndValueSek)}
          </div>
          <div className="s">cumulative, end of horizon</div>
        </div>
      </div>

      <div className="statbar">
        <div className="stat"><div className="v">{activeTasks.length}{inactiveCount ? <span style={{fontSize:12,color:"var(--muted)"}}> / {tasks.length}</span> : null}</div><div className="k">Active initiatives</div></div>
        <div className="stat"><div className="v">{totalsByStatus.Live||0}</div><div className="k">Live</div></div>
        <div className="stat"><div className="v">{totalsByStatus.Building||0}</div><div className="k">Building</div></div>
        <div className="stat"><div className="v">{totalsByStatus.Planned||0}</div><div className="k">Planned</div></div>
        <div className="stat"><div className="v">{totalsByStatus.Future||0}</div><div className="k">Future</div></div>
        <div className="stat"><div className="v">{totalsByPrio.P1||0} <span style={{fontSize:12,color:"var(--muted)"}}>P1</span></div><div className="k">Quick Wins</div></div>
        <div className="stat"><div className="v">{totalsByPrio.P4||0} <span style={{fontSize:12,color:"var(--muted)"}}>P4</span></div><div className="k">Long-term plays</div></div>
        {inactiveCount > 0 && (
          <div className="stat"><div className="v" style={{color:"#fca5a5"}}>{inactiveCount}</div><div className="k">Disabled</div></div>
        )}
      </div>

      <h2 style={{marginTop:32, marginBottom:12}}>Departments</h2>
      <div className="grid cols-3">
        {["Sales","PS","PD"].map(k => <DeptCard key={k} dept={k} info={meta.departments[k]} tasks={tasks} settings={settings}/>)}
      </div>
    </div>
  );
}

function DeptCard({ dept, info, tasks, settings }) {
  const deptTasks   = tasks.filter(t => t.deptShort === dept);
  const activeDept  = deptTasks.filter(t => t.active !== false);
  const inactive    = deptTasks.length - activeDept.length;
  const counts = { P1:0, P2:0, P3:0, P4:0, Live:0, Building:0, Planned:0, Future:0 };
  activeDept.forEach(t => { counts[t.priority]++; counts[t.status]++; });
  const port = portfolioSummary(deptTasks, settings);

  return (
    <a href={"#/dept/"+dept} className="deptcard" style={{display:"block", color:"inherit"}}>
      <div className="swatch" style={{background: info.color}}/>
      <div className="full">{info.fullname}</div>
      <h3 style={{marginTop:2}}>{info.name} <span style={{fontWeight:400, color:"var(--muted)", fontSize:13}}>· {activeDept.length} active{inactive ? ` (${inactive} disabled)` : ""}</span></h3>
      <div className="headline">{info.headline}</div>

      <div className="kpi-row" style={{gridTemplateColumns:"1fr 1fr 1fr", marginTop:6, gap:8}}>
        <div className="kpi" style={{padding:10}}>
          <div className="l">Build cost</div>
          <div className="v" style={{fontSize:16}}>{fmtSek(port.totalCostSek)}</div>
        </div>
        <div className="kpi" style={{padding:10}}>
          <div className="l">Monthly value</div>
          <div className="v positive" style={{fontSize:16}}>{fmtSek(port.totalMonthlyValueSek)}</div>
        </div>
        <div className="kpi" style={{padding:10}}>
          <div className="l">Hours saved /mo</div>
          <div className="v positive" style={{fontSize:16}}>{fmtHours(port.totalMonthlyHoursSaved)}</div>
        </div>
        <div className="kpi" style={{padding:10}}>
          <div className="l">Saved-hours value /mo</div>
          <div className="v positive" style={{fontSize:16}}>{fmtSek(port.totalHoursSavedValueSek)}</div>
        </div>
        <div className="kpi" style={{padding:10}}>
          <div className="l">New revenue /mo</div>
          <div className="v positive" style={{fontSize:16}}>{fmtSek(port.totalNewRevenueMonthlySek)}</div>
        </div>
        <div className="kpi" style={{padding:10}}>
          <div className="l">New rev. to Dec 2027</div>
          <div className="v positive" style={{fontSize:16}}>{fmtSek(port.totalNewRevenueHorizonSek)}</div>
        </div>
        <div className="kpi" style={{padding:10}}>
          <div className="l">Avg score</div>
          <div className="v" style={{fontSize:16}}>{port.avgScore.toFixed(1)}</div>
        </div>
      </div>

      <div className="stats" style={{marginTop:14}}>
        <span><b>{counts.P1}</b> Quick wins</span>
        <span><b>{counts.P2}</b> High</span>
        <span><b>{counts.P3}</b> Medium</span>
        <span><b>{counts.P4}</b> Long-term</span>
      </div>
      <div className="stats" style={{margin:0}}>
        <span><b>{counts.Live}</b> Live</span>
        <span><b>{counts.Building}</b> Building</span>
        <span><b>{counts.Planned}</b> Planned</span>
        <span><b>{counts.Future}</b> Future</span>
      </div>

      <ul className="themes">{info.main_themes.map((t,i) => <li key={i}>{t}</li>)}</ul>
      <span className="openlink">Open {info.name}</span>
    </a>
  );
}
